import {
  ColumnVisibleEvent,
  DragStoppedEvent,
  GridOptions,
  RowDataChangedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { GridChangeEvent } from 'components/organisms/grid-custom-column';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  EstimatedDueDateRenderer,
  Last5StatusUpdatesCellRenderer,
  ProgressPercentCellRenderer,
  ProjectHoursCellRenderer,
  ProjectPhasesCellRenderer,
  ProjectNameRenderer,
  RatingRenderer,
} from './cell-renderers';
import { groupRowInnerRenderer } from './group-inner-renderer';
import { getDefaultGroupSortComparator } from './group-sort-comparator';
import { NoResultProjectListEmptyState } from './no-result';
import { overlayLoadingTemplate } from './overlay-loading-template';

export const PROJECT_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY =
  'project_list.v2.column_state';

const getDefaultOptions = (
  handleGridChange: (
    event:
      | SortChangedEvent
      | DragStoppedEvent
      | ColumnVisibleEvent
      | RowDataChangedEvent,
  ) => void,
): GridOptions => ({
  tooltipShowDelay: 200,
  suppressContextMenu: true,
  unSortIcon: true,
  groupSuppressAutoColumn: true,
  immutableData: true,
  debounceVerticalScrollbar: true,
  frameworkComponents: {
    ProjectNameRenderer,
    ProjectHoursCellRenderer,
    ProjectPhasesCellRenderer,
    ProgressPercentCellRenderer,
    EstimatedDueDateRenderer,
    Last5StatusUpdatesCellRenderer,
    NoResultProjectListEmptyState,
    RatingRenderer,
  },
  components: {
    groupRowInnerRenderer,
  },
  rowBuffer: 8,
  defaultColDef: {
    resizable: true,
    sortable: true,
    editable: false,
    lockPinned: true,
    suppressMovable: true,
    suppressMenu: true,
  },
  groupUseEntireRow: true,
  groupRowRendererParams: {
    checkbox: false,
  },
  groupDefaultExpanded: 1,
  getRowNodeId(d) {
    return d.projectId.toString();
  },
  onSortChanged: handleGridChange,
  onColumnVisible: handleGridChange,
  onColumnRowGroupChanged: handleGridChange,
  onColumnMoved: handleGridChange,
});

export const useGridOptions = (
  columnFields: FilterableField[],
  handleGridChange: (data: GridChangeEvent) => void,
): GridOptions => {
  const onGridChange = (event: GridChangeEvent) => {
    handleGridChange(event);
  };

  const defaultGridOptions = getDefaultOptions(onGridChange);

  return useMemo(() => {
    const fieldLookByFieldName = keyBy(columnFields, 'fieldName');

    return {
      ...defaultGridOptions,
      defaultGroupSortComparator:
        getDefaultGroupSortComparator(fieldLookByFieldName),
      groupRowRendererParams: {
        ...defaultGridOptions.groupRowRendererParams,
        innerRenderer: 'groupRowInnerRenderer',
        fieldLookByFieldName,
      },
      overlayLoadingTemplate,
      noRowsOverlayComponent: 'NoResultProjectListEmptyState',
    };
    // we have to re-run memo only when columnFields changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFields]);
};
